import type { AuthCredentials } from './types.js';
import { WIRE_VERSION } from './types.js';

export function buildHeaders(creds: AuthCredentials, nonce: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Public-Key': creds.publicKey,
    'X-Nonce': nonce,
    'X-Signature': creds.signature,
    'X-Wire-Version': WIRE_VERSION,
  };
  if (creds.bearerToken !== undefined) {
    headers['Authorization'] = `Bearer ${creds.bearerToken}`;
  }
  return headers;
}

// Decimal Unix milliseconds; valid for 2 minutes either side of server clock.
export function freshNonce(now: () => number = Date.now): string {
  return String(now());
}

/**
 * A monotonic nonce source (W1). `Date.now()` has millisecond resolution, so two requests
 * from one keypair in the same ms produce an identical `(client_id, nonce)` and the gateway
 * rejects the second as a replay (401) — which `requestWithRetry` does NOT retry. At app
 * start a handful of aggregates open at once and hit exactly this. The nonce must stay a
 * parseable u64 ms-timestamp within the server's freshness window, so we bump by 1ms on
 * collision rather than adding entropy: `max(lastNonce + 1, now)`, per-source monotonic.
 * Drift past `now` is bounded by the burst size and stays well inside the +/-2min window.
 */
export function createNonceSource(now: () => number = Date.now): () => string {
  let last = 0;
  return () => {
    const candidate = now();
    last = candidate > last ? candidate : last + 1;
    return String(last);
  };
}
